import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiSlash, FiRefreshCw, FiCheck, FiDroplet } from 'react-icons/fi';
import {
  getDonationCategoriesAll,
  createDonationCategory,
  updateDonationCategory,
  deleteDonationCategory,
  hardDeleteDonationCategory,
  reactivateDonationCategory,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import styles from './DonationCategories.module.css';

import { fmtCurrency } from '../../utils/formatNumber';

/** Prevent mouse-wheel scroll from changing a focused number input's value */
const blockWheel = (e) => e.target.blur();

/** Block decimal point / exponent / sign keys so only whole numbers can be typed */
const blockNonInteger = (e) => {
  if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
};

/** Coerce any input to a whole-number string (truncates decimals, e.g. pasted "501.44" → "501") */
const toIntString = (v) => String(v ?? '').split('.')[0].replace(/[^\d]/g, '');

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#06b6d4', '#ec4899'];
const EMPTY  = {
  name: '',
  description: '',
  defaultValue: '',
  defaultQty: '',
  colorCode: '',
  commissionEnabled: false,
  commissionPercent: '',
  commissionAmount: '',
};

const DonationCategories = () => {
  const { activeDerasarId, can } = useAuth();

  const [categories, setCategories] = useState([]);
  const [filtered, setFiltered]     = useState([]);
  const [search, setSearch]         = useState('');
  const [tab, setTab]               = useState('active'); // 'active' | 'deactivated'
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(null); // null | { mode:'add'|'edit', data }
  const [actionTarget, setActionTarget] = useState(null); // null | { cat, type: 'deactivate' | 'delete' }
  const [saving, setSaving]         = useState(false);

  const load = useCallback(async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const res = await getDonationCategoriesAll(activeDerasarId);
      setCategories(res.data);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.toLowerCase();
    const pool = categories.filter(c => tab === 'active' ? c.isActive : !c.isActive);
    let result = q
        ? pool.filter(c => c.name.toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q))
        : pool;
    setFiltered(result.sort((a,b) => a.name.localeCompare(b.name)));
  }, [search, categories, tab]);

  const openAdd  = () => setModal({ mode: 'add',  data: { ...EMPTY } });
  const openEdit = (cat) => setModal({ mode: 'edit', data: {
    id: cat.id,
    name: cat.name,
    description: cat.description || '',
    defaultValue: cat.defaultValue || '',
    defaultQty: cat.defaultQty != null ? String(cat.defaultQty) : '',
    colorCode: cat.colorCode || '',
    isDefault: cat.isDefault || false,
    commissionEnabled: cat.commissionEnabled || false,
    commissionPercent: cat.commissionPercent != null ? String(cat.commissionPercent) : '',
    commissionAmount: cat.commissionAmount != null ? String(cat.commissionAmount) : '',
  }});

  const patch = (field, value) =>
    setModal(m => ({ ...m, data: { ...m.data, [field]: value } }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { id, ...d } = modal.data;
    if (d.commissionEnabled && `${d.commissionPercent ?? ''}`.trim() === '' && `${d.commissionAmount ?? ''}`.trim() === '') {
      toast.error('Enter commission rate or commission value');
      setSaving(false);
      return;
    }
    try {
      const payload = {
        name: d.name,
        description: d.description || undefined,
        defaultValue: d.defaultValue || undefined,
        defaultQty: d.defaultQty !== '' ? Number(d.defaultQty) : null,
        colorCode: d.colorCode || undefined,
        commissionEnabled: d.commissionEnabled,
        commissionPercent: d.commissionEnabled && d.commissionPercent !== '' ? Number(d.commissionPercent) : null,
        commissionAmount: d.commissionEnabled && d.commissionAmount !== '' ? Number(d.commissionAmount) : null,
      };
      if (modal.mode === 'add') {
        await createDonationCategory({ derasarId: activeDerasarId, ...payload });
        toast.success('Category created');
      } else {
        await updateDonationCategory(id, payload);
        toast.success('Category updated');
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async () => {
    const { cat, type } = actionTarget;
    try {
      if (type === 'deactivate') {
        await deleteDonationCategory(cat.id);
        toast.success('Category deactivated');
      } else if (type === 'delete') {
        await hardDeleteDonationCategory(cat.id);
        toast.success('Category permanently deleted');
      } else if (type === 'reactivate') {
        await reactivateDonationCategory(cat.id);
        toast.success('Category reactivated');
      }
      setActionTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    }
  };

  if (!activeDerasarId) {
    return <div className="empty-state"><p>Select a derasar to manage categories</p></div>;
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className="page-header">
        <h2>Donation Categories</h2>
        {can('donationCategories', 'manage') && (
          <button className="btn btn-primary btn-sm" onClick={openAdd}>
            <FiPlus /> New Category
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tabBtn} ${tab === 'active' ? styles.tabActive : ''}`}
            onClick={() => setTab('active')}
          >
            Active
            <span className={styles.tabCount}>{categories.filter(c => c.isActive).length}</span>
          </button>
          <button
            className={`${styles.tabBtn} ${tab === 'deactivated' ? styles.tabActive : ''}`}
            onClick={() => setTab('deactivated')}
          >
            Deactivated
            <span className={styles.tabCount}>{categories.filter(c => !c.isActive).length}</span>
          </button>
        </div>
        <div className={styles.searchBox}>
          <FiSearch className={styles.searchIcon} />
          <input
            placeholder="Search categories…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Default Qty</th>
                  <th>Default Rate (₹)</th>
                  <th>Commission</th>
                  <th style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">
                        {search ? 'No categories match your search.' : 'No categories yet. Click "New Category" to create one.'}
                      </div>
                    </td>
                  </tr>
                )}
                {filtered.map((cat, i) => (
                  <tr key={cat.id} className={!cat.isActive ? styles.rowDeactivated : undefined}>
                    <td className={styles.idx}>{i + 1}</td>
                    <td>
                      <div className={styles.nameCell}>
                        {cat.colorCode && (
                          <span className={styles.dot} style={{ background: cat.colorCode, opacity: cat.isActive ? 1 : 0.4 }} />
                        )}
                        <span className={styles.name}>{cat.name}</span>
                        {cat.isDefault && <span className="badge badge-admin">default</span>}
                        {!cat.isActive && <span className="badge" style={{ background: '#e5e7eb', color: '#6b7280' }}>deactivated</span>}
                      </div>
                    </td>
                    <td className={styles.desc}>{cat.description || '—'}</td>
                    <td>{cat.defaultQty != null ? cat.defaultQty : '—'}</td>
                    <td>{cat.defaultValue ? fmtCurrency(cat.defaultValue) : '—'}</td>
                    <td>
                      {!cat.commissionEnabled
                        ? '—'
                        : cat.commissionAmount != null && cat.commissionAmount !== ''
                          ? fmtCurrency(cat.commissionAmount)
                          : `${Number(cat.commissionPercent || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}
                    </td>
                    <td>
                      <div className={styles.actions}>
                        {can('donationCategories', 'manage') && (cat.isActive ? (
                          <>
                            <button className="btn btn-ghost btn-sm" onClick={() => openEdit(cat)} title="Edit">
                              <FiEdit2 size={14} />
                            </button>
                            {!cat.isDefault && (
                              <>
                                <button
                                  className="btn btn-warning btn-sm"
                                  onClick={() => setActionTarget({ cat, type: 'deactivate' })}
                                  title="Deactivate"
                                >
                                  <FiSlash size={14} />
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => setActionTarget({ cat, type: 'delete' })}
                                  title="Delete permanently"
                                >
                                  <FiTrash2 size={14} />
                                </button>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => setActionTarget({ cat, type: 'reactivate' })}
                              title="Reactivate"
                            >
                              <FiRefreshCw size={14} />
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => setActionTarget({ cat, type: 'delete' })}
                              title="Delete permanently"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="card-header">
              <h3>{modal.mode === 'add' ? 'New Category' : 'Edit Category'}</h3>
            </div>
            <form onSubmit={handleSave}>
              <div className={styles.modalBody}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    required
                    value={modal.data.name}
                    onChange={e => patch('name', e.target.value)}
                    placeholder="e.g., Dev Dravya"
                    readOnly={modal.data.isDefault}
                    style={modal.data.isDefault ? { background: 'var(--color-bg-subtle, #f3f4f6)', cursor: 'not-allowed' } : undefined}
                  />
                  {modal.data.isDefault && (
                    <p className={styles.hint}>Default category names cannot be changed.</p>
                  )}
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input
                    value={modal.data.description}
                    onChange={e => patch('description', e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Default Qty</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className={styles.noSpin}
                      value={modal.data.defaultQty}
                      onWheel={blockWheel}
                      onKeyDown={blockNonInteger}
                      onChange={e => patch('defaultQty', toIntString(e.target.value))}
                      placeholder="e.g. 2"
                    />
                  </div>
                  <div className="form-group">
                    <label>Default Rate (₹)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={styles.noSpin}
                      value={modal.data.defaultValue}
                      onWheel={blockWheel}
                      onChange={e => patch('defaultValue', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <div className={styles.toggleLabel}>
                    <span>Charge Commission</span>
                    <label className={styles.toggle}>
                      <input
                        type="checkbox"
                        checked={modal.data.commissionEnabled}
                        onChange={e => patch('commissionEnabled', e.target.checked)}
                      />
                      <span className={styles.toggleSlider} />
                    </label>
                  </div>
                </div>
                {modal.data.commissionEnabled && (
                  <div className={styles.commissionGrid}>
                    <div className="form-group">
                      <label>Commission Rate (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className={styles.noSpin}
                        value={modal.data.commissionPercent}
                        onWheel={blockWheel}
                        onChange={e => {
                          patch('commissionPercent', e.target.value);
                          if (`${e.target.value}`.trim() !== '') patch('commissionAmount', '');
                        }}
                        placeholder="e.g. 10 for 10%"
                      />
                    </div>
                    <div className="form-group">
                      <label>Commission Value (?)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={styles.noSpin}
                        value={modal.data.commissionAmount}
                        onWheel={blockWheel}
                        onChange={e => {
                          patch('commissionAmount', e.target.value);
                          if (`${e.target.value}`.trim() !== '') patch('commissionPercent', '');
                        }}
                        placeholder="e.g. 100.00"
                      />
                    </div>
                    <p className={styles.commissionNote}>
                      Fill either rate or value. If value is set, it will be used for commission income.
                    </p>
                  </div>
                )}
                <div className="form-group">
                  <label>Color</label>
                  <div
                    className={styles.colorPreview}
                    style={{ background: modal.data.colorCode || '#e5e7eb' }}
                  />
                  <div className={styles.swatchGrid}>
                    {COLORS.map(c => {
                      const active = modal.data.colorCode === c;
                      return (
                        <button
                          key={c} type="button"
                          className={`${styles.swatch} ${active ? styles.swatchActive : ''}`}
                          style={{ background: c }}
                          onClick={() => patch('colorCode', c)}
                          title={c}
                        >
                          {active && <FiCheck size={13} color="#fff" strokeWidth={3} />}
                        </button>
                      );
                    })}
                    <label className={styles.customSwatch} title="Pick a custom color">
                      <FiDroplet size={14} />
                      <span>Custom</span>
                      <input
                        type="color"
                        value={modal.data.colorCode || '#6366f1'}
                        onChange={e => patch('colorCode', e.target.value)}
                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                      />
                    </label>
                  </div>
                </div>
                {modal.mode === 'add' && (
                  <p className={styles.hint}>
                    Donation will be recorded under Donation income.
                  </p>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : modal.mode === 'add' ? 'Create' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Action Confirmation Modal */}
      {actionTarget && (() => {
        const { cat, type } = actionTarget;
        const isReactivate = type === 'reactivate';
        const isDelete     = type === 'delete';
        const title   = isReactivate ? 'Reactivate Category' : isDelete ? 'Delete Category' : 'Deactivate Category';
        const verb    = isReactivate ? 'Reactivate' : isDelete ? 'Permanently delete' : 'Deactivate';
        const detail  = isReactivate
          ? 'It will be available again for new receipts and offerings.'
          : isDelete
          ? 'This will permanently remove the category and its linked ledger accounts. This action cannot be undone.'
          : 'It will be hidden from new receipts and offerings. Existing transactions are not affected.';
        const btnClass = isReactivate ? 'btn btn-success' : 'btn btn-danger';
        return (
          <div className="modal-overlay" onClick={() => setActionTarget(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="card-header"><h3>{title}</h3></div>
              <div style={{ padding: '16px 20px' }}>
                <p>{verb} <strong>{cat.name}</strong>?</p>
                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{detail}</p>
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={() => setActionTarget(null)}>Cancel</button>
                <button className={btnClass} onClick={handleAction}>{isReactivate ? 'Reactivate' : isDelete ? 'Delete' : 'Deactivate'}</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default DonationCategories;
